
//functions
import * as Functions from '../functions/common';

// models
import { ProtocolConsumptionSyncProcess } from '../models/Entities/Protocols/ProtocolConsumptionSyncProcess';
import { ProtocolConsumptionSyncProcessLog } from '../models/Entities/Protocols/ProtocolConsumptionSyncProcessLog';
import { IResult } from '../models/HttpResult';
import { IPagedResult } from '../models/HttpPagedResult';
import { ISyncProcessLogsFilters } from '../models/Filters/SyncProcessLogsFilters';
import { SyncProcessLogSeverityEnum } from '../models/Enums/SyncProcessLogSeverityEnum';

/**
 * Umbral de registro con el que corre una sincronización si no se elige otro.
 * Coincide con el default del backend: se guardan avisos y errores, y la traza
 * fina (Info) queda afuera.
 */
export const DEFAULT_MIN_LOG_SEVERITY = SyncProcessLogSeverityEnum.Warning;

var BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'protocols/protocols/';

// get all sync process by protocol id
export const GetProtocolConsumptionSyncProcess = async (
    protocolId: number,
    token: string
): Promise<ProtocolConsumptionSyncProcess[]> => {
    let RequestURL: string = BaseURL + protocolId + '/consumptions/sync';
    try {
        const Response: ProtocolConsumptionSyncProcess[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionSyncProcess[]>>;
            }
        })
        .then((data: IResult<ProtocolConsumptionSyncProcess[]>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// get protocol consumption sync process by id

export const GetProtocolConsumptionSyncProcessById = async (
    token: string,
    id: string
): Promise<ProtocolConsumptionSyncProcess> => {
    let RequestURL: string = BaseURL + 'consumptions/sync/' + id;
    try {
        const Response: ProtocolConsumptionSyncProcess = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionSyncProcess>>;
            }
        })
        .then((data: IResult<ProtocolConsumptionSyncProcess>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

/** Tamaño de página por defecto y máximo que acepta el endpoint. */
export const SYNC_PROCESS_LOGS_MAX_PAGE_SIZE = 500;

/**
 * Logs de un proceso de sincronización: paginados y filtrados por el servidor.
 *
 * Devuelve el envelope completo, no sólo `data`: la pantalla necesita
 * `totalRecords` y `totalPages` para poder recorrer el listado.
 */
export const GetProtocolConsumptionSyncProcessLogsById = async (
    token: string,
    id: string,
    pageNumber: number,
    pageSize: number,
    filters?: ISyncProcessLogsFilters
): Promise<IPagedResult<ProtocolConsumptionSyncProcessLog[]>> => {

    const params = new URLSearchParams();
    params.append('pageNumber', String(pageNumber));
    params.append('pageSize', String(Math.min(pageSize, SYNC_PROCESS_LOGS_MAX_PAGE_SIZE)));

    // Se comparan contra undefined y no por verdadero: Info (0) y General (0)
    // son filtros válidos que una comprobación por truthiness descartaría.
    if (filters?.severity !== undefined) {
        params.append('severity', String(filters.severity));
    }
    if (filters?.subjectType !== undefined) {
        params.append('subjectType', String(filters.subjectType));
    }
    if (filters?.patientNumber?.trim()) {
        params.append('patientNumber', filters.patientNumber.trim());
    }
    if (filters?.searchText?.trim()) {
        params.append('searchText', filters.searchText.trim());
    }

    let RequestURL: string = BaseURL + 'consumptions/sync/' + id + '/logs?' + params.toString();

    try {
        const Response: IPagedResult<ProtocolConsumptionSyncProcessLog[]> = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IPagedResult<ProtocolConsumptionSyncProcessLog[]>>;
            }
        })
        .then((data: IPagedResult<ProtocolConsumptionSyncProcessLog[]>) => {
            return data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// get last protocol consumption sync process

export const GetLastProtocolConsumptionSyncProcess = async (
    protocolId: number,
    token: string
): Promise<ProtocolConsumptionSyncProcess> => {
    let RequestURL: string = BaseURL + protocolId + '/consumptions/sync/last';
    try {
        const Response: ProtocolConsumptionSyncProcess = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionSyncProcess>>;
            }
        })
        .then((data: IResult<ProtocolConsumptionSyncProcess>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Add protocol consumption sync process

export const AddProtocolConsumptionSyncProcess = async (
    protocolId: number,
    useMedicalPayableDefinitions: boolean,
    token: string,
    minLogSeverity: SyncProcessLogSeverityEnum = DEFAULT_MIN_LOG_SEVERITY
): Promise<ProtocolConsumptionSyncProcess> => {
    let RequestURL: string = BaseURL + 'consumptions/sync';
    try {
        const Response: ProtocolConsumptionSyncProcess = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'POST',
        body: JSON.stringify({
            protocolId: protocolId,
            useMedicalPayableDefinition: useMedicalPayableDefinitions,
            minLogSeverity: minLogSeverity
        })
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionSyncProcess>>;
            }
        })
        .then((data: IResult<ProtocolConsumptionSyncProcess>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// update protocol consumption sync process

export const UpdateProtocolConsumptionSyncProcess = async (
    token: string,
    SyncProcess: ProtocolConsumptionSyncProcess
): Promise<ProtocolConsumptionSyncProcess> => {
    let RequestURL: string = BaseURL + 'consumptions/sync/';
    try {
        const Response: ProtocolConsumptionSyncProcess = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(SyncProcess),
        method: 'PUT'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionSyncProcess>>;
            }
        })
        .then((data: IResult<ProtocolConsumptionSyncProcess>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// delete protocol consumption sync process

export const DeleteProtocolConsumptionSyncProcess = async (
    token: string,
    id: string
): Promise<ProtocolConsumptionSyncProcess> => {
    let RequestURL: string = BaseURL + 'consumptions/sync/' + id;
    try {
        const Response: ProtocolConsumptionSyncProcess = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'DELETE'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionSyncProcess>>;
            }
        })
        .then((data: IResult<ProtocolConsumptionSyncProcess>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

